import {DispatchOrder} from "../../types/DispatchOrder";
import {NoActivityReport} from "../../types/NoActivityReport";
import {User} from "../../types/User";
import {ActivityMaterial} from "../../types/ActivityMaterial";
import {SelectOption} from "./CommonUIElementTypes";
import {Constants} from "./Constants";
import {DateUtility} from "./DateUtility";

export interface ActivityListItemModelsByUser {
    [userId: number]: {
        user: User,
        dispatchOrders: DispatchOrder[],
        noActivityReports: NoActivityReport[],
    }
}

export let DispatchOrdersUtility = {
    getActivityListItemModelsByUser: function (dispatchOrdersList: DispatchOrder[], noActivityReportsList: NoActivityReport[]) {
        let activityListItemModelsByUser: ActivityListItemModelsByUser = {};

        for (let i = 0; i < dispatchOrdersList.length; i++) {
            const dispatchOrder = dispatchOrdersList[i];
            let user = dispatchOrder.requested_by_user;

            if (!activityListItemModelsByUser[user.id]) {
                activityListItemModelsByUser[user.id] = {
                    user: user,
                    dispatchOrders: [],
                    noActivityReports: [],
                }
            }

            activityListItemModelsByUser[user.id].dispatchOrders.push(dispatchOrder)
        }

        for (let i = 0; i < noActivityReportsList.length; i++) {
            const noActivityReport = noActivityReportsList[i];
            let user = noActivityReport.user;

            if (!activityListItemModelsByUser[user.id]) {
                activityListItemModelsByUser[user.id] = {
                    user: user,
                    dispatchOrders: [],
                    noActivityReports: [],
                }
            }

            activityListItemModelsByUser[user.id].noActivityReports.push(noActivityReport)
        }

        return activityListItemModelsByUser;
    },

    selectableMaterialOptionsForScope: function (
        activityMaterials: ActivityMaterial[],
        activityScopeId: number | null | undefined,
        savedActivityMaterial?: ActivityMaterial
    ): SelectOption[] {
        let selectableOptions: SelectOption[] = [];

        if (activityScopeId == null) {
            return selectableOptions;
        }

        for (let activityMaterial of (activityMaterials || [])) {
            let isLinkedToScope = (activityMaterial.activity_scopes || []).some((activityScope) => activityScope.id === activityScopeId);

            if (isLinkedToScope) {
                selectableOptions.push({
                    value: activityMaterial.id,
                    label: activityMaterial.name,
                });
            }
        }

        // The material a saved record already references stays offered even when the active-only pool no longer carries it, so an existing order still edits and saves.
        if (savedActivityMaterial && !selectableOptions.some((option) => option.value === savedActivityMaterial.id)) {
            selectableOptions.push({
                value: savedActivityMaterial.id,
                label: savedActivityMaterial.name,
            });
        }

        return selectableOptions;
    },

    brokerHasEarlierOrEqualOppositeDirectionAssignment: function (activityBrokerAssignment: any, activity: any, activities: any[]): boolean {
        const thisAssignmentStartTime = activityBrokerAssignment.startTimeJsDate;

        if (!activityBrokerAssignment.broker_id || !thisAssignmentStartTime) {
            return false;
        }

        // Compare at minute granularity so two opposite-direction assignments a user set to the same minute count as equal even when their underlying Dates differ in seconds.
        const thisAssignmentStartMinute = DateUtility.jsDateToMoment(thisAssignmentStartTime).startOf('minute').valueOf();

        const oppositeDirectionName =
            activity.activity_direction?.name === Constants.activityDirectionNames.inbound ?
                Constants.activityDirectionNames.outbound :
                Constants.activityDirectionNames.inbound;

        return (activities || []).some((otherActivity: any) =>
            otherActivity.activity_direction?.name === oppositeDirectionName &&
            (otherActivity.activity_broker_assignments || []).some((otherAssignment: any) =>
                '' + otherAssignment.broker_id === '' + activityBrokerAssignment.broker_id &&
                otherAssignment.startTimeJsDate &&
                DateUtility.jsDateToMoment(otherAssignment.startTimeJsDate).startOf('minute').valueOf() <= thisAssignmentStartMinute &&
                // Round-robin is offered only between two assignments with matching pit-haul status (both pit hauls, or both non-pit-hauls).
                !!activityBrokerAssignment.pit_haul === !!otherAssignment.pit_haul
            )
        );
    },

    // The counted (non-pit-haul) truck sum, with a trailing "+" appended when any assignment counting toward the total is a pit haul; when the counted sum is zero and a pit haul is present the display is just "+".
    formatOrderTruckTotalDisplay: function (totalTruckCount: number, hasAnyPitHaul: boolean): string {
        let countDisplay = totalTruckCount > 0 ? '' + totalTruckCount : '';
        return hasAnyPitHaul ? countDisplay + '+' : countDisplay;
    },
}
